/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
const PAYMENT_REFERENCE_MEMO_PREFIX = 'Payment Reference:';

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getLineItemStartDate = (item) =>
    item?.scheduledProduct?.period?.effectiveDate?.localStartDate
    || item?.scheduledProduct?.period?.effectiveStartDate
    || '';

const getLineItemEndDate = (item) =>
    item?.scheduledProduct?.period?.effectiveDate?.localEndDate
    || item?.scheduledProduct?.period?.effectiveEndDate
    || '';

const normalizeMetadataToken = (value) =>
    String(value || '')
        .trim()
        .replace(/[\s-]+/g, '_')
        .toUpperCase();

const getLineItemProduct = (lineItem) =>
    lineItem?.product || lineItem?.scheduledProduct?.product || null;

const hasScheduledBookingReference = (lineItem) =>
    Boolean(
        lineItem?.scheduledProduct?.primaryId
        || lineItem?.scheduledProduct?.period
        || lineItem?.scheduledProduct?.product?.primaryId
        || lineItem?.scheduledProduct?.name
    );

const getLineItemIdentityKey = (lineItem) => {
    const scheduledProductId = lineItem?.scheduledProduct?.primaryId;
    const productId = lineItem?.scheduledProduct?.product?.primaryId || getLineItemProduct(lineItem)?.primaryId;

    if (scheduledProductId) {
        return `scheduled:${scheduledProductId}`;
    }

    if (productId) {
        return `product:${productId}`;
    }

    return '';
};

const getLineItemMetadataTokens = (lineItem) => {
    const product = getLineItemProduct(lineItem) || {};
    const category = product?.category || {};
    const type = product?.type || {};

    return [
        lineItem?.productCategorySubType,
        product?.productCategorySubType,
        category?.productCategorySubType,
        category?.subType,
        category?.type,
        category?.code,
        category?.name,
        type?.typeCode,
        type?.name,
    ]
        .map(normalizeMetadataToken)
        .filter(Boolean);
};

const isLateFeeToken = (token) =>
    token === 'LATE_FEE' || token.endsWith('_LATE_FEE') || token.includes('LATE_FEE');

const isLateFeeLineItem = (lineItem) =>
    getLineItemMetadataTokens(lineItem).some(isLateFeeToken);

const isAddOnLineItem = (lineItem) => {
    if (!lineItem || isLateFeeLineItem(lineItem)) {
        return false;
    }

    const product = getLineItemProduct(lineItem);
    const hasProductReference = Boolean(product?.primaryId || product?.name);

    return hasProductReference && !hasScheduledBookingReference(lineItem);
};

const getLineItemRate = (item) => {
    const quantity = Number(item?.quantity) || 1;
    const directRate = item?.product?.price?.priceAmt
        || item?.scheduledProduct?.product?.price?.priceAmt
        || item?.priceAmt
        || item?.rate;

    if (directRate !== undefined && directRate !== null && directRate !== '') {
        return Number(directRate) || 0;
    }

    const totalAmount = Number(item?.totalAmount);
    if (Number.isFinite(totalAmount) && quantity > 0) {
        return totalAmount / quantity;
    }

    return 0;
};

export const getChildOrdersFromOrder = (order) =>
    Array.isArray(order?.childOrders) ? order.childOrders.filter(Boolean) : [];

export const isGroupedOrderWithChildren = (order) =>
    Boolean(order?.isGroupedOrder) && getChildOrdersFromOrder(order).length > 0;

export const getOrderLineItems = (order) => {
    if (!order) return [];

    if (Array.isArray(order?.lineItemList) && order.lineItemList.length > 0) {
        return order.lineItemList.filter(Boolean);
    }

    if (Array.isArray(order?.orderDetails?.[0]?.lineItemList) && order.orderDetails[0].lineItemList.length > 0) {
        return order.orderDetails[0].lineItemList.filter(Boolean);
    }

    if (isGroupedOrderWithChildren(order)) {
        return getChildOrdersFromOrder(order).flatMap((childOrder) => getOrderLineItems(childOrder));
    }

    return [];
};

export const getOrderSpecialItemFlags = (order) => {
    const lineItems = getOrderLineItems(getReceiptCompatibleOrder(order));

    if (!lineItems.length) {
        return {
            hasAddOnItems: false,
            hasLateFeeItems: false,
        };
    }

    const scheduledBookingKeys = new Set(
        lineItems
            .filter(hasScheduledBookingReference)
            .map(getLineItemIdentityKey)
            .filter(Boolean)
    );

    const hasLateFeeItems = lineItems.some((lineItem) => isLateFeeLineItem(lineItem));
    const hasAddOnItems = lineItems.some((lineItem) => {
        if (!lineItem || isLateFeeLineItem(lineItem)) {
            return false;
        }

        const product = getLineItemProduct(lineItem);
        const hasProductReference = Boolean(product?.primaryId || product?.name);

        if (!hasProductReference && !hasScheduledBookingReference(lineItem)) {
            return false;
        }

        if (!hasScheduledBookingReference(lineItem)) {
            return true;
        }

        const identityKey = getLineItemIdentityKey(lineItem);
        return Boolean(identityKey) && !scheduledBookingKeys.has(identityKey);
    });

    return {
        hasAddOnItems,
        hasLateFeeItems,
    };
};

export const getOrderAddOnLineItems = (order) =>
    getOrderLineItems(getReceiptCompatibleOrder(order)).filter(isAddOnLineItem);

const getPrimaryBookingLineItem = (order) => {
    const lineItems = getOrderLineItems(getReceiptCompatibleOrder(order));

    return lineItems.find((lineItem) =>
        hasScheduledBookingReference(lineItem) && !isLateFeeLineItem(lineItem)
    )
        || lineItems.find(hasScheduledBookingReference)
        || lineItems[0]
        || null;
};

const buildGroupedReceiptLineItems = (order) => {
    if (!isGroupedOrderWithChildren(order)) {
        return getOrderLineItems(order);
    }

    const groupedLineItems = new Map();

    getChildOrdersFromOrder(order).forEach((childOrder) => {
        getOrderLineItems(childOrder).forEach((lineItem, index) => {
            const product = lineItem?.scheduledProduct?.product || lineItem?.product || {};
            const scheduledProduct = lineItem?.scheduledProduct || {};
            const period = scheduledProduct?.period || {};
            const rate = getLineItemRate(lineItem);
            const quantity = Number(lineItem?.quantity) || 1;
            const totalAmount = Number(lineItem?.totalAmount);
            const amount = Number.isFinite(totalAmount) ? totalAmount : rate * quantity;
            const key = [
                product?.primaryId || product?.name || scheduledProduct?.primaryId || scheduledProduct?.name || `item-${index}`,
                rate,
                period?.duration || '',
                period?.durationType || '',
                product?.category?.name || ''
            ].join('::');
            const existingItem = groupedLineItems.get(key);

            if (!existingItem) {
                groupedLineItems.set(key, {
                    ...lineItem,
                    quantity,
                    totalAmount: roundMoney(amount),
                    groupedSessionCount: 1,
                    scheduledProduct: scheduledProduct
                        ? {
                            ...scheduledProduct,
                            period: period
                                ? {
                                    ...period,
                                    effectiveDate: {
                                        ...(period?.effectiveDate || {}),
                                        localStartDate: getLineItemStartDate(lineItem),
                                        localEndDate: getLineItemEndDate(lineItem)
                                    }
                                }
                                : period
                        }
                        : scheduledProduct
                });
                return;
            }

            const existingStartDate = getLineItemStartDate(existingItem);
            const existingEndDate = getLineItemEndDate(existingItem);
            const nextStartDate = getLineItemStartDate(lineItem);
            const nextEndDate = getLineItemEndDate(lineItem);

            groupedLineItems.set(key, {
                ...existingItem,
                quantity: roundMoney(Number(existingItem.quantity || 0) + quantity),
                totalAmount: roundMoney(Number(existingItem.totalAmount || 0) + amount),
                groupedSessionCount: Number(existingItem.groupedSessionCount || 0) + 1,
                scheduledProduct: existingItem?.scheduledProduct
                    ? {
                        ...existingItem.scheduledProduct,
                        period: existingItem?.scheduledProduct?.period
                            ? {
                                ...existingItem.scheduledProduct.period,
                                effectiveDate: {
                                    ...(existingItem?.scheduledProduct?.period?.effectiveDate || {}),
                                    localStartDate:
                                        existingStartDate && nextStartDate
                                            ? (new Date(nextStartDate) < new Date(existingStartDate) ? nextStartDate : existingStartDate)
                                            : nextStartDate || existingStartDate,
                                    localEndDate:
                                        existingEndDate && nextEndDate
                                            ? (new Date(nextEndDate) > new Date(existingEndDate) ? nextEndDate : existingEndDate)
                                            : nextEndDate || existingEndDate
                                }
                            }
                            : existingItem?.scheduledProduct?.period
                    }
                    : existingItem?.scheduledProduct
            });
        });
    });

    return Array.from(groupedLineItems.values());
};

export const getReceiptCompatibleOrder = (order) => {
    if (!order) return order;

    const directLineItems = Array.isArray(order?.lineItemList) ? order.lineItemList.filter(Boolean) : [];
    if (directLineItems.length > 0) {
        return order;
    }

    if (!isGroupedOrderWithChildren(order)) {
        return order;
    }

    const recurringLineItems = buildGroupedReceiptLineItems(order);
    if (!recurringLineItems.length) {
        return order;
    }

    return {
        ...order,
        lineItemList: recurringLineItems
    };
};

export const getBestReceiptOrder = (...orders) => {
    const normalizedOrders = orders
        .filter(Boolean)
        .map((order) => getReceiptCompatibleOrder(order));

    return normalizedOrders.find((order) => isGroupedOrderWithChildren(order) && getOrderLineItems(order).length > 0)
        || normalizedOrders.find((order) => getOrderLineItems(order).length > 0)
        || normalizedOrders[0]
        || null;
};

export const getProductFromOrder = (order) => {
    const lineItem = getPrimaryBookingLineItem(order);
    if (lineItem?.scheduledProduct?.product) {
        return lineItem.scheduledProduct.product;
    } else if (lineItem?.product) {
        return lineItem.product;
    }
    return null;
};

export const getScheduledProductFromOrder = (order) => {
    const lineItem = getPrimaryBookingLineItem(order);
    return lineItem?.scheduledProduct;
};

const getPaymentReferenceFromMemo = (memo) => {
    const normalizedMemo = String(memo || '').trim();
    if (!normalizedMemo) return '';

    const prefixIndex = normalizedMemo.toLowerCase().indexOf(PAYMENT_REFERENCE_MEMO_PREFIX.toLowerCase());
    if (prefixIndex === -1) return '';

    return normalizedMemo
        .slice(prefixIndex + PAYMENT_REFERENCE_MEMO_PREFIX.length)
        .trim();
};

const getPaymentReferenceFromTransactions = (transactionList = []) => {
    const purchaseTransactions = Array.isArray(transactionList)
        ? transactionList.filter((transaction) => transaction?.type === 'PURCHASE')
        : [];
    const transactionsToCheck = purchaseTransactions.length > 0 ? purchaseTransactions : transactionList;

    return transactionsToCheck.reduce((referenceId, transaction) => {
        if (referenceId) return referenceId;

        return String(
            transaction?.paymentRefId
            || transaction?.transactionId
            || transaction?.referenceId
            || ''
        ).trim();
    }, '');
};

export const getOrderPaymentReference = (order) => {
    if (!order) return '';

    const inheritedParentReference = String(order?.parentPaymentReference || '').trim();
    if (inheritedParentReference) return inheritedParentReference;

    const directTransactionReference = getPaymentReferenceFromTransactions(order.transactionList || []);
    if (directTransactionReference) return directTransactionReference;

    const memoReference = getPaymentReferenceFromMemo(order.memo);
    if (memoReference) return memoReference;

    if (isGroupedOrderWithChildren(order)) {
        return getOrderPaymentReference(getChildOrdersFromOrder(order)[0]);
    }

    return '';
};

const getTransactionPaymentMethodCode = (transaction) => {
    const paymentMethodData = transaction?.paymentMethod;
    const methodName = String(paymentMethodData?.name || '').trim().toUpperCase();
    const methodType = String(paymentMethodData?.paymentMethodType || '').trim().toUpperCase();

    if (methodName.includes('ZELLE')) {
        return 'ZELLE';
    }

    return methodType;
};

// An order's transactionList can hold more than one entry (e.g. an initial CASH
// placeholder alongside the real ZELLE purchase), so pick the one that actually
// represents the purchase rather than just transactionList[0]. Mirrors
// admin-console-ui's getAssociatedPaymentTransaction (order-management/index.jsx).
export const getOrderPurchaseTransaction = (order) => {
    const transactionList = Array.isArray(order?.transactionList) ? order.transactionList.filter(Boolean) : [];
    const purchaseTransactions = transactionList.filter((transaction) => transaction?.type === 'PURCHASE');
    const candidates = purchaseTransactions.length > 0 ? purchaseTransactions : transactionList;
    if (!candidates.length) return null;

    const status = String(order?.currentStatus || order?.status || '').toUpperCase();
    if (status === 'PAYMENT_PROCESSING') {
        const zelleTransaction = candidates.find((transaction) => {
            const code = getTransactionPaymentMethodCode(transaction);
            return code === 'ZELLE' || code === 'PAYMENT_APP' || code === 'BANK_ACCOUNT';
        });
        if (zelleTransaction) return zelleTransaction;
    }
    if (status === 'PAYMENT_PENDING') {
        const cashTransaction = candidates.find((transaction) => getTransactionPaymentMethodCode(transaction) === 'CASH');
        if (cashTransaction) return cashTransaction;
    }

    return candidates.find((transaction) => transaction?.paymentRefId) || candidates[0];
};

export const getOrderPaymentMethodCode = (order) => {
    if (!order) return '';

    const directPaymentMethod = String(order?.paymentMethod || '').trim().toUpperCase();
    if (directPaymentMethod) {
        return directPaymentMethod;
    }

    const transactionMethodCode = getTransactionPaymentMethodCode(getOrderPurchaseTransaction(order));
    if (transactionMethodCode) {
        return transactionMethodCode;
    }

    // Recurring/child orders don't carry their own PURCHASE transaction — it's recorded
    // only on the parent (grouped) order that was actually paid. Mirrors the fallback
    // already used by getOrderPaymentReference above.
    if (order?.parentOrder) {
        return getOrderPaymentMethodCode(order.parentOrder);
    }

    if (isGroupedOrderWithChildren(order)) {
        return getOrderPaymentMethodCode(getChildOrdersFromOrder(order)[0]);
    }

    return '';
};

export const isOrderCashPayment = (order) => getOrderPaymentMethodCode(order) === 'CASH';

export const isOrderZellePayment = (order) => {
    const paymentMethodCode = getOrderPaymentMethodCode(order);
    return paymentMethodCode === 'ZELLE'
        || paymentMethodCode === 'PAYMENT_APP'
        || paymentMethodCode === 'BANK_ACCOUNT';
};

export const getCategoryName = (order) => {
    const product = getProductFromOrder(order);
    if (product?.category?.name) {
        return product.category.name;
    }
    const scheduledProduct = getScheduledProductFromOrder(order);
    if (scheduledProduct?.product?.category?.name) {
        return scheduledProduct.product.category.name;
    }
    return '';
};

export const getCleanProductName = (order, defaultLabel = 'Booking') => {
    const scheduledProduct = getScheduledProductFromOrder(order);
    const product = getProductFromOrder(order);

    let productName = '';

    if (scheduledProduct?.name) {
        productName = scheduledProduct.name.split(' - ')[0];
    } else if (product?.name) {
        productName = product.name;
    }

    return productName || defaultLabel || 'Untitled Booking';
};

export const getProductImage = (order) => {
    const product = getProductFromOrder(order);
    if (!product) {
        const scheduledProduct = getScheduledProductFromOrder(order);
        if (scheduledProduct?.product) {
            return getProductImageFromProduct(scheduledProduct.product);
        }
        return null;
    }
    return getProductImageFromProduct(product);
};

export const getProductImageFromProduct = (product) => {
    if (product?.imageContents?.[0]?.displayLink) {
        return product.imageContents[0].displayLink;
    }
    if (product?.imageUrlsList?.[0]) {
        return product.imageUrlsList[0].startsWith('http')
            ? product.imageUrlsList[0]
            : `https://managemybiz.s3.amazonaws.com/${product.imageUrlsList[0]}`;
    }
    if (product?.category?.imageContent?.displayLink) {
        return product.category.imageContent.displayLink;
    }
    return null;
};

export const getBookingDuration = (order, zoneId, formatDateInCompanyTimezone, formatTimeInCompanyTimezone) => {
    const lineItems = getOrderLineItems(getReceiptCompatibleOrder(order));
    if (!lineItems.length) return null;

    const bookingPeriods = lineItems
        .map((lineItem) => lineItem?.scheduledProduct?.period)
        .filter((period) => period?.effectiveDate?.localStartDate && period?.effectiveDate?.localEndDate)
        .sort((leftPeriod, rightPeriod) =>
            new Date(leftPeriod.effectiveDate.localStartDate) - new Date(rightPeriod.effectiveDate.localStartDate)
        );

    if (!bookingPeriods.length) {
        return null;
    }

    try {
        const firstPeriod = bookingPeriods[0];
        const lastPeriod = bookingPeriods[bookingPeriods.length - 1];
        const firstStartDateValue = firstPeriod.effectiveDate.localStartDate;
        const firstEndDateValue = firstPeriod.effectiveDate.localEndDate;
        const lastStartDateValue = lastPeriod.effectiveDate.localStartDate;
        const lastEndDateValue = lastPeriod.effectiveDate.localEndDate;
        const startDate = new Date(firstStartDateValue);
        const endDate = new Date(firstEndDateValue);
        const isGroupedOrder = isGroupedOrderWithChildren(order);
        const firstDisplayDate = formatDateInCompanyTimezone(firstStartDateValue);
        const lastDisplayDate = formatDateInCompanyTimezone(lastStartDateValue);
        const dateStr = isGroupedOrder && firstDisplayDate !== lastDisplayDate
            ? `${firstDisplayDate} - ${lastDisplayDate}`
            : firstDisplayDate;
        const startTime = formatTimeInCompanyTimezone(firstStartDateValue);
        const endTime = formatTimeInCompanyTimezone(firstEndDateValue);
        const hasMeaningfulTime = !(startTime === '12:00 AM' && endTime === '12:00 AM');
        const durationMs = endDate - startDate;
        const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 100) / 100;
        const sessionCount = isGroupedOrder ? getChildOrdersFromOrder(order).length : 1;
        const durationType = String(firstPeriod?.durationType || '').toLowerCase();
        const durationLabel = firstPeriod?.duration
            ? `${firstPeriod.duration} ${durationType ? `${durationType}${Number(firstPeriod.duration) === 1 ? '' : 's'}` : `hour${Number(firstPeriod.duration) === 1 ? '' : 's'}`}`
            : `${durationHours} hour${durationHours !== 1 ? 's' : ''}`;
        const durationStr = isGroupedOrder && sessionCount > 1
            ? `${sessionCount} sessions${durationLabel ? ` • ${durationLabel} each` : ''}`
            : durationLabel;

        return {
            dateStr,
            startTime,
            endTime,
            hasMeaningfulTime,
            durationHours,
            durationStr,
            sessionCount,
            isGroupedOrder,
            rangeEndDate: formatDateInCompanyTimezone(lastStartDateValue),
            timeZone: firstPeriod.effectiveDate.timeZone || lastPeriod.effectiveDate.timeZone || zoneId,
            lastEndDate: lastEndDateValue
        };
    } catch (error) {
        return null;
    }
};

export const getStatusConfig = (status) => {
    const configs = {
        PAID: {
            color: 'text-green-600',
            bg: 'bg-green-50',
            border: 'border-green-200',
            label: 'Paid'
        },
        CANCELLED: {
            color: 'text-red-600',
            bg: 'bg-red-50',
            border: 'border-red-200',
            label: 'Cancelled'
        },
        REFUND_PENDING: {
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            label: 'Refund Pending'
        },
        PAYMENT_PENDING: {
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            label: 'Payment Pending'
        },
        PAYMENT_PROCESSING: {
            color: 'text-amber-700',
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            label: 'Payment Processing'
        }
    };

    return configs[status] || {
        color: 'text-gray-500',
        bg: 'bg-gray-100',
        border: 'border-gray-200',
        label: status
    };
};
